import { Link, NavLink, useNavigate } from "react-router-dom";
import { Scale, LayoutDashboard, FolderOpen, ShieldCheck, CreditCard, LogOut, Globe, ChevronRight, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/cases", label: "Cases", icon: FolderOpen, testid: "nav-cases" },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-audit" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function AppLayout({ children }) {
  const { user, agency, isAdmin, logout } = useAuth();
  const nav = useNavigate();

  const trialDaysLeft = (() => {
    if (!agency?.trial_ends_at) return null;
    const ms = new Date(agency.trial_ends_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  })();

  const portalUrl = agency ? `${window.location.origin}/submit/${agency.agency_id}` : null;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <Link to="/dashboard" className="h-16 px-5 flex items-center gap-2 border-b border-slate-200" data-testid="sidebar-brand">
          <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
          <span className="font-serif text-xl font-medium">FOIA Assist</span>
        </Link>

        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Agency</div>
          <div className="font-semibold text-sm truncate" data-testid="sidebar-agency-name">{agency?.name || "—"}</div>
          {agency?.subscription_status === "trial" && trialDaysLeft !== null && (
            <div className="mt-3 fa-badge fa-badge-warning" data-testid="trial-badge">
              {trialDaysLeft} days left in trial
            </div>
          )}
          {agency?.subscription_status === "active" && (
            <div className="mt-3 fa-badge fa-badge-success">{agency.plan?.toUpperCase()} · Active</div>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-[#0B1B3D] text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors border-t border-slate-100 mt-2 pt-3 ${
                  isActive ? "bg-[#C5A059]/20 text-[#0B1B3D] font-medium" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <ShieldAlert className="w-4 h-4 text-[#C5A059]" strokeWidth={1.75} />
              Admin
            </NavLink>
          )}
        </nav>

        {portalUrl && (
          <div className="mx-3 mb-3 p-3 fa-card-muted text-xs">
            <div className="flex items-center gap-2 mb-1.5 text-slate-700 font-semibold">
              <Globe className="w-3.5 h-3.5" strokeWidth={1.75} /> Public portal
            </div>
            <a href={portalUrl} target="_blank" rel="noreferrer" className="text-[#0B1B3D] underline decoration-[#C5A059] underline-offset-2 break-all" data-testid="public-portal-link">
              Open ↗
            </a>
          </div>
        )}

        <div className="border-t border-slate-200 p-4">
          <div className="text-xs text-slate-500 mb-1">{user?.name}</div>
          <div className="text-xs text-slate-400 mb-3 truncate">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); nav("/"); }}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-red-600"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}

export function Crumbs({ items }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-6">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {it.to ? <Link to={it.to} className="hover:text-slate-900">{it.label}</Link> : <span className="text-slate-900">{it.label}</span>}
          {i < items.length - 1 && <ChevronRight className="w-3 h-3" strokeWidth={1.75} />}
        </span>
      ))}
    </div>
  );
}
